import * as Sentry from "@sentry/react"
import { ErrorComponentProps, LinkProps } from "@tanstack/react-router"
import { Button, ButtonLink } from "design-system/atoms/Button"
import { LucideIcon, SignpostBig, Telescope } from "lucide-react"
import { useAuth } from "modules/shared/hooks/useAuth"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import Page from "../Page"

type Props = {
  title: string
  description: string
  icon?: LucideIcon
  cta: {
    variant?: React.ComponentProps<typeof ButtonLink>["variant"]
    text: string
  } & (LinkProps | { onClick: () => void })
}

function NotFound({ title, description, icon: Icon, cta }: Props) {
  const { text, variant, ...otherProps } = cta

  return (
    <div className="flex flex-col text-center items-center gap-12 w-full py-12">
      {Icon && <Icon size={120} strokeWidth={0.5} className="text-foreground" />}
      <div className="flex flex-col text-center items-center gap-2 w-full">
        <h1 className="text-2xl">{title}</h1>
        <p className="text-base">{description}</p>
      </div>
      {"onClick" in cta ? (
        <Button onClick={cta.onClick} variant={variant ?? "outline"}>
          <span>{text}</span>
        </Button>
      ) : (
        <ButtonLink
          {...otherProps}
          search={(prev: unknown) => (cta.to?.includes(location.pathname) && prev) || {}}
          variant={variant ?? "outline"}
        >
          <span>{text}</span>
        </ButtonLink>
      )}
    </div>
  )
}

export function RouteNotFound() {
  const { t } = useTranslation()
  const { authStatus } = useAuth()

  if (authStatus === "authenticated") {
    return (
      <Page>
        <NotFound
          title={t("shared.notFound.title")}
          description={t("shared.notFound.description")}
          icon={SignpostBig}
          cta={{
            to: "/",
            text: t("shared.notFound.goHome"),
          }}
        />
      </Page>
    )
  }

  return (
    <div className="flex flex-col text-center items-center gap-8">
      <NotFound
        title={t("shared.notFound.title")}
        description={t("shared.notFound.description")}
        icon={SignpostBig}
        cta={{
          to: "/",
          text: t("shared.notFound.goHome"),
        }}
      />
    </div>
  )
}

export function RouteError({
  error,
  info,
  showPage = true,
}: { showPage?: boolean } & Omit<ErrorComponentProps, "reset">) {
  const { t } = useTranslation()
  useEffect(() => {
    Sentry.captureException(error, {
      contexts: {
        componentStack: {
          stack: info?.componentStack,
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const content = (
    <NotFound
      title={t("shared.error.title")}
      description={t("shared.error.description")}
      icon={Telescope}
      cta={{
        onClick: () => window.location.reload(),
        text: t("shared.error.reload"),
      }}
    />
  )

  if (showPage) {
    return <Page>{content}</Page>
  }

  return content
}
